import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useSiteContent } from "../context/SiteContentContext";
import { api, setAuthToken } from "../lib/api";

const STORAGE_KEY = "admin_token";
const inputClass = "bg-slate-950 border-slate-800 text-slate-100 placeholder:text-slate-500";
const textareaClass = "bg-slate-950 border-slate-800 text-slate-100 placeholder:text-slate-500";

function nextId(items) {
  const max = (items || []).reduce((acc, item) => {
    const value = Number(item?.id);
    return Number.isFinite(value) ? Math.max(acc, value) : acc;
  }, 0);
  return max + 1;
}

function ensureString(value) {
  return value == null ? "" : String(value);
}

function ButtonSecondaryFix({ children, className = "", ...props }) {
  return (
    <Button
      variant="secondary"
      className={`text-slate-100 bg-white/10 hover:bg-white/15 border border-white/10 ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { siteContent, refresh } = useSiteContent();
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("company");
  const [draft, setDraft] = useState(siteContent);
  const [jsonText, setJsonText] = useState("");
  const [editingPostId, setEditingPostId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setAuthToken(token);
  }, [navigate]);

  useEffect(() => {
    setDraft(siteContent);
    setJsonText(JSON.stringify(siteContent, null, 2));
  }, [siteContent]);

  useEffect(() => {
    if (tab !== "blogPosts") return;
    const posts = draft?.blogPosts;
    if (!Array.isArray(posts) || posts.length === 0) return;
    if (editingPostId == null) setEditingPostId(posts[0]?.id ?? null);
  }, [tab, draft?.blogPosts, editingPostId]);

  const parsed = useMemo(() => {
    try {
      return JSON.parse(jsonText);
    } catch {
      return null;
    }
  }, [jsonText]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthToken(null);
    navigate("/admin/login");
  };

  const save = async () => {
    const payload = tab === "json" ? parsed : draft;
    if (!payload) return toast.error("Conteúdo inválido");

    try {
      setSaving(true);
      await api.put("/admin/site-content", { data: payload });
      toast.success("Conteúdo salvo");
      await refresh();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessão expirada, faça login novamente");
        logout();
        return;
      }
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const updateCompanyInfo = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      companyInfo: { ...(prev.companyInfo || {}), [key]: value },
    }));
  };

  const addClientAreaLink = () => {
    setDraft((prev) => {
      const companyInfo = prev.companyInfo || {};
      const links = Array.isArray(companyInfo.clientAreaLinks) ? companyInfo.clientAreaLinks : [];
      return {
        ...prev,
        companyInfo: {
          ...companyInfo,
          clientAreaLinks: [...links, { label: "Nova opção", href: "" }],
        },
      };
    });
  };

  const updateClientAreaLink = (index, patch) => {
    setDraft((prev) => {
      const companyInfo = prev.companyInfo || {};
      const links = Array.isArray(companyInfo.clientAreaLinks) ? companyInfo.clientAreaLinks : [];
      return {
        ...prev,
        companyInfo: {
          ...companyInfo,
          clientAreaLinks: links.map((item, i) => (i === index ? { ...item, ...patch } : item)),
        },
      };
    });
  };

  const removeClientAreaLink = (index) => {
    setDraft((prev) => {
      const companyInfo = prev.companyInfo || {};
      const links = Array.isArray(companyInfo.clientAreaLinks) ? companyInfo.clientAreaLinks : [];
      return {
        ...prev,
        companyInfo: {
          ...companyInfo,
          clientAreaLinks: links.filter((_, i) => i !== index),
        },
      };
    });
  };

  const updateImages = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      images: { ...(prev.images || {}), [key]: value },
    }));
  };

  const updateInstitutionalVideo = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      institutionalVideo: { ...(prev.institutionalVideo || {}), [key]: value },
    }));
  };

  const updateContactSection = (section, patch) => {
    setDraft((prev) => {
      const contactPage = prev.contactPage || {};
      const current = contactPage[section] || {};
      return {
        ...prev,
        contactPage: {
          ...contactPage,
          [section]: { ...current, ...patch },
        },
      };
    });
  };

  const updateContactListItem = (section, listKey, index, patch) => {
    setDraft((prev) => {
      const contactPage = prev.contactPage || {};
      const current = contactPage[section] || {};
      const list = Array.isArray(current[listKey]) ? current[listKey] : [];
      const next = list.map((item, i) => (i === index ? { ...item, ...patch } : item));
      return {
        ...prev,
        contactPage: {
          ...contactPage,
          [section]: { ...current, [listKey]: next },
        },
      };
    });
  };

  const addContactListItem = (section, listKey, item) => {
    setDraft((prev) => {
      const contactPage = prev.contactPage || {};
      const current = contactPage[section] || {};
      const list = Array.isArray(current[listKey]) ? current[listKey] : [];
      return {
        ...prev,
        contactPage: {
          ...contactPage,
          [section]: { ...current, [listKey]: [...list, item] },
        },
      };
    });
  };

  const removeContactListItem = (section, listKey, index) => {
    setDraft((prev) => {
      const contactPage = prev.contactPage || {};
      const current = contactPage[section] || {};
      const list = Array.isArray(current[listKey]) ? current[listKey] : [];
      return {
        ...prev,
        contactPage: {
          ...contactPage,
          [section]: { ...current, [listKey]: list.filter((_, i) => i !== index) },
        },
      };
    });
  };

  const updateAboutSection = (section, patch) => {
    setDraft((prev) => {
      const aboutPage = prev.aboutPage || {};
      const current = aboutPage[section] || {};
      return {
        ...prev,
        aboutPage: {
          ...aboutPage,
          [section]: { ...current, ...patch },
        },
      };
    });
  };

  const updateAboutListItem = (section, listKey, index, patch) => {
    setDraft((prev) => {
      const aboutPage = prev.aboutPage || {};
      const current = aboutPage[section] || {};
      const list = Array.isArray(current[listKey]) ? current[listKey] : [];
      const next = list.map((item, i) => (i === index ? { ...item, ...patch } : item));
      return {
        ...prev,
        aboutPage: {
          ...aboutPage,
          [section]: { ...current, [listKey]: next },
        },
      };
    });
  };

  const addAboutListItem = (section, listKey, item) => {
    setDraft((prev) => {
      const aboutPage = prev.aboutPage || {};
      const current = aboutPage[section] || {};
      const list = Array.isArray(current[listKey]) ? current[listKey] : [];
      return {
        ...prev,
        aboutPage: {
          ...aboutPage,
          [section]: { ...current, [listKey]: [...list, item] },
        },
      };
    });
  };

  const removeAboutListItem = (section, listKey, index) => {
    setDraft((prev) => {
      const aboutPage = prev.aboutPage || {};
      const current = aboutPage[section] || {};
      const list = Array.isArray(current[listKey]) ? current[listKey] : [];
      return {
        ...prev,
        aboutPage: {
          ...aboutPage,
          [section]: { ...current, [listKey]: list.filter((_, i) => i !== index) },
        },
      };
    });
  };

  const uploadImage = async (key, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateImages(key, `${origin}/uploads/${filename}`);
      toast.success("Upload concluído");
    } catch (err) {
      toast.error("Falha no upload");
    }
  };

  const uploadBlogImage = async (postId, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateListItemById("blogPosts", postId, { image: `${origin}/uploads/${filename}` });
      toast.success("Upload concluÇðdo");
    } catch (err) {
      toast.error("Falha no upload");
    }
  };

  const uploadInstitutionalCover = async (file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateInstitutionalVideo("coverUrl", `${origin}/uploads/${filename}`);
      toast.success("Upload concluÇðdo");
    } catch {
      toast.error("Falha no upload");
    }
  };

  const uploadAboutVideoCover = async (file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateAboutSection("video", { coverUrl: `${origin}/uploads/${filename}` });
      toast.success("Upload concluído");
    } catch {
      toast.error("Falha no upload");
    }
  };

  const uploadInstitutionalVideoFile = async (file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-media", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateInstitutionalVideo("videoUrl", `${origin}/uploads/${filename}`);
      toast.success("Upload concluÇðdo");
    } catch {
      toast.error("Falha no upload");
    }
  };

  const uploadAboutVideoFile = async (file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-media", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateAboutSection("video", { videoUrl: `${origin}/uploads/${filename}` });
      toast.success("Upload concluído");
    } catch {
      toast.error("Falha no upload");
    }
  };

  const uploadTestimonialPhoto = async (testimonialId, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateListItemById("testimonials", testimonialId, { photo: `${origin}/uploads/${filename}` });
      toast.success("Upload concluído");
    } catch {
      toast.error("Falha no upload");
    }
  };

  const updateListItem = (listKey, index, patch) => {
    setDraft((prev) => {
      const list = Array.isArray(prev[listKey]) ? prev[listKey] : [];
      const next = list.map((item, i) => (i === index ? { ...item, ...patch } : item));
      return { ...prev, [listKey]: next };
    });
  };

  const updateListItemById = (listKey, id, patch) => {
    setDraft((prev) => {
      const list = Array.isArray(prev[listKey]) ? prev[listKey] : [];
      const next = list.map((item) => (Number(item?.id) === Number(id) ? { ...item, ...patch } : item));
      return { ...prev, [listKey]: next };
    });
  };

  const removeListItem = (listKey, index) => {
    setDraft((prev) => {
      const list = Array.isArray(prev[listKey]) ? prev[listKey] : [];
      return { ...prev, [listKey]: list.filter((_, i) => i !== index) };
    });
  };

  const addServiceFeature = (serviceIndex, text) => {
    const value = ensureString(text).trim();
    if (!value) return;
    setDraft((prev) => {
      const services = Array.isArray(prev.services) ? prev.services : [];
      const service = services[serviceIndex] || {};
      const features = Array.isArray(service.features) ? service.features : [];
      return {
        ...prev,
        services: services.map((s, idx) =>
          idx === serviceIndex ? { ...s, features: [...features, value] } : s
        ),
      };
    });
  };

  const removeServiceFeature = (serviceIndex, featureIndex) => {
    setDraft((prev) => {
      const services = Array.isArray(prev.services) ? prev.services : [];
      const service = services[serviceIndex] || {};
      const features = Array.isArray(service.features) ? service.features : [];
      return {
        ...prev,
        services: services.map((s, idx) =>
          idx === serviceIndex
            ? { ...s, features: features.filter((_, i) => i !== featureIndex) }
            : s
        ),
      };
    });
  };

  const uploadServiceImage = async (serviceIndex, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filename = res?.data?.filename;
      if (!filename) throw new Error("missing filename");
      const base = api?.defaults?.baseURL || "";
      const origin = new URL(base, window.location.href).origin;
      updateListItem("services", serviceIndex, { image: `${origin}/uploads/${filename}` });
      toast.success("Upload concluído");
    } catch {
      toast.error("Falha no upload");
    }
  };

  const addService = () => {
    setDraft((prev) => {
      const services = Array.isArray(prev.services) ? prev.services : [];
      return {
        ...prev,
        services: [
          ...services,
          { id: nextId(services), title: "Novo serviço", description: "", icon: "Users", features: [] },
        ],
      };
    });
  };

  const addDifferential = () => {
    setDraft((prev) => {
      const differentials = Array.isArray(prev.differentials) ? prev.differentials : [];
      return {
        ...prev,
        differentials: [
          ...differentials,
          { id: nextId(differentials), title: "Novo diferencial", description: "", icon: "Zap" },
        ],
      };
    });
  };

  const addTestimonial = () => {
    setDraft((prev) => {
      const testimonials = Array.isArray(prev.testimonials) ? prev.testimonials : [];
      return {
        ...prev,
        testimonials: [
          ...testimonials,
          { id: nextId(testimonials), name: "", role: "", company: "", content: "", photo: "" },
        ],
      };
    });
  };

  const addBlogPost = () => {
    const newId = nextId(draft?.blogPosts || []);
    setDraft((prev) => {
      const blogPosts = Array.isArray(prev.blogPosts) ? prev.blogPosts : [];
      const today = new Date().toISOString().slice(0, 10);
      const id = nextId(blogPosts);
      return {
        ...prev,
        blogPosts: [
          ...blogPosts,
          { id, title: "", slug: "", excerpt: "", content: "", date: today, category: "Blog", image: "" },
        ],
      };
    });
    setEditingPostId(newId);
  };

  const addBlogCategory = (name) => {
    const value = ensureString(name).trim();
    if (!value) return;
    setDraft((prev) => {
      const existing = Array.isArray(prev.blogCategories) ? prev.blogCategories : [];
      if (existing.some((c) => String(c).toLowerCase() === value.toLowerCase())) return prev;
      return { ...prev, blogCategories: [...existing, value] };
    });
  };

  const removeBlogCategory = (name) => {
    setDraft((prev) => {
      const existing = Array.isArray(prev.blogCategories) ? prev.blogCategories : [];
      return { ...prev, blogCategories: existing.filter((c) => c !== name) };
    });
  };

  const addFaq = () => {
    setDraft((prev) => {
      const faqs = Array.isArray(prev.faqs) ? prev.faqs : [];
      return {
        ...prev,
        faqs: [...faqs, { question: "", answer: "" }],
      };
    });
  };

  const syncJsonFromDraft = () => setJsonText(JSON.stringify(draft, null, 2));
  const applyJsonToDraft = () => {
    if (!parsed) return toast.error("JSON inválido");
    setDraft(parsed);
    toast.success("JSON aplicado na dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin do site</h1>
            <p className="text-sm text-slate-300">Edite tudo em uma dashboard ou no modo JSON.</p>
          </div>
          <div className="flex items-center gap-2">
            <ButtonSecondaryFix onClick={refresh}>
              Recarregar
            </ButtonSecondaryFix>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
            <Button variant="ghost" onClick={logout}>
              Sair
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="company">Empresa</TabsTrigger>
            <TabsTrigger value="about">Quem Somos</TabsTrigger>
            <TabsTrigger value="services">Serviços</TabsTrigger>
            <TabsTrigger value="contact">Contato</TabsTrigger>
            <TabsTrigger value="differentials">Diferenciais</TabsTrigger>
            <TabsTrigger value="testimonials">Depoimentos</TabsTrigger>
            <TabsTrigger value="blogPosts">Blog</TabsTrigger>
            <TabsTrigger value="faqs">FAQ</TabsTrigger>
            <TabsTrigger value="video">Vídeo</TabsTrigger>
            <TabsTrigger value="images">Imagens</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="company" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <h2 className="text-lg font-semibold text-slate-100">Empresa</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Nome</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.name)}
                      onChange={(e) => updateCompanyInfo("name", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Nome completo</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.fullName)}
                      onChange={(e) => updateCompanyInfo("fullName", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs text-slate-300">Slogan</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.slogan)}
                      onChange={(e) => updateCompanyInfo("slogan", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs text-slate-300">Descrição</label>
                    <Textarea
                      value={ensureString(draft?.companyInfo?.description)}
                      onChange={(e) => updateCompanyInfo("description", e.target.value)}
                      className={`min-h-24 ${textareaClass}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">WhatsApp (DDI+DDD+número)</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.whatsapp)}
                      onChange={(e) => updateCompanyInfo("whatsapp", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Telefone</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.phone)}
                      onChange={(e) => updateCompanyInfo("phone", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">E-mail</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.email)}
                      onChange={(e) => updateCompanyInfo("email", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Mensagem do WhatsApp</label>
                    <Input
                      value={ensureString(draft?.companyInfo?.whatsappMessage)}
                      onChange={(e) => updateCompanyInfo("whatsappMessage", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-3 md:col-span-2">
                    <div className="flex items-center justify-between gap-3">
                      <label className="text-xs text-slate-300">Área do cliente (dropdown)</label>
                      <ButtonSecondaryFix onClick={addClientAreaLink}>Adicionar</ButtonSecondaryFix>
                    </div>
                    <div className="space-y-3">
                      {(draft?.companyInfo?.clientAreaLinks || []).map((item, idx) => (
                        <div
                          key={`${item?.label || "opcao"}-${idx}`}
                          className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
                        >
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Nome</label>
                            <Input
                              value={ensureString(item?.label)}
                              onChange={(e) => updateClientAreaLink(idx, { label: e.target.value })}
                              className={inputClass}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Link</label>
                            <Input
                              value={ensureString(item?.href)}
                              onChange={(e) => updateClientAreaLink(idx, { href: e.target.value })}
                              className={inputClass}
                              placeholder="https://..."
                            />
                          </div>
                          <Button variant="ghost" onClick={() => removeClientAreaLink(idx)}>
                            Remover
                          </Button>
                        </div>
                      ))}
                      {(draft?.companyInfo?.clientAreaLinks || []).length === 0 ? (
                        <div className="text-xs text-slate-400">Sem opções ainda.</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs text-slate-300">Estados atendidos (1 por linha)</label>
                    <Textarea
                      value={Array.isArray(draft?.companyInfo?.coverage) ? draft.companyInfo.coverage.join("\n") : ""}
                      onChange={(e) =>
                        updateCompanyInfo(
                          "coverage",
                          e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                        )
                      }
                      className={`min-h-20 font-mono text-xs ${textareaClass}`}
                      spellCheck={false}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="about" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-6">
                <h2 className="text-lg font-semibold text-slate-100">Quem Somos</h2>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Hero</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.hero?.badge)}
                        onChange={(e) => updateAboutSection("hero", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título (parte 1)</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.hero?.title)}
                        onChange={(e) => updateAboutSection("hero", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título (destaque)</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.hero?.highlight)}
                        onChange={(e) => updateAboutSection("hero", { highlight: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.aboutPage?.hero?.description)}
                        onChange={(e) => updateAboutSection("hero", { description: e.target.value })}
                        className={`min-h-24 ${textareaClass}`}
                      />
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Introdução</h3>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Título</label>
                    <Input
                      value={ensureString(draft?.aboutPage?.intro?.title)}
                      onChange={(e) => updateAboutSection("intro", { title: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Parágrafos (1 por linha)</label>
                    <Textarea
                      value={Array.isArray(draft?.aboutPage?.intro?.paragraphs)
                        ? draft.aboutPage.intro.paragraphs.join("\n")
                        : ""}
                      onChange={(e) =>
                        updateAboutSection(
                          "intro",
                          { paragraphs: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) }
                        )
                      }
                      className={`min-h-28 ${textareaClass}`}
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <label className="text-xs text-slate-300">Destaques (ícone + texto)</label>
                      <ButtonSecondaryFix
                        onClick={() => addAboutListItem("intro", "stats", { icon: "MapPin", label: "" })}
                      >
                        Adicionar
                      </ButtonSecondaryFix>
                    </div>
                    <div className="space-y-3">
                      {(draft?.aboutPage?.intro?.stats || []).map((item, idx) => (
                        <div key={`${item?.label || "stat"}-${idx}`} className="grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Ícone</label>
                            <Input
                              value={ensureString(item?.icon)}
                              onChange={(e) => updateAboutListItem("intro", "stats", idx, { icon: e.target.value })}
                              className={inputClass}
                              placeholder="MapPin, Users..."
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Texto</label>
                            <Input
                              value={ensureString(item?.label)}
                              onChange={(e) => updateAboutListItem("intro", "stats", idx, { label: e.target.value })}
                              className={inputClass}
                            />
                          </div>
                          <Button variant="ghost" onClick={() => removeAboutListItem("intro", "stats", idx)}>
                            Remover
                          </Button>
                        </div>
                      ))}
                      {(draft?.aboutPage?.intro?.stats || []).length === 0 ? (
                        <div className="text-xs text-slate-400">Sem destaques ainda.</div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Vídeo</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.video?.badge)}
                        onChange={(e) => updateAboutSection("video", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.video?.title)}
                        onChange={(e) => updateAboutSection("video", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.aboutPage?.video?.description)}
                        onChange={(e) => updateAboutSection("video", { description: e.target.value })}
                        className={`min-h-24 ${textareaClass}`}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Capa do vídeo (URL)</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.video?.coverUrl)}
                        onChange={(e) => updateAboutSection("video", { coverUrl: e.target.value })}
                        className={inputClass}
                        placeholder="https://..."
                      />
                      {draft?.aboutPage?.video?.coverUrl ? (
                        <img
                          src={draft.aboutPage.video.coverUrl}
                          alt="Capa do vídeo institucional"
                          className="w-full max-w-xl h-48 object-cover rounded-md border border-slate-800"
                          loading="lazy"
                        />
                      ) : null}
                      <Input
                        type="file"
                        accept="image/*"
                        className={inputClass}
                        onChange={(e) => uploadAboutVideoCover(e.target.files?.[0])}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Link do vídeo (mp4/webm/mov, YouTube ou Vimeo)</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.video?.videoUrl)}
                        onChange={(e) => updateAboutSection("video", { videoUrl: e.target.value })}
                        className={inputClass}
                        placeholder="https://..."
                      />
                      <Input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        className={inputClass}
                        onChange={(e) => uploadAboutVideoFile(e.target.files?.[0])}
                      />
                      <div className="text-[11px] text-slate-400">
                        Upload salva em `/uploads` no backend; link externo também funciona.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Pilares</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.pillars?.badge)}
                        onChange={(e) => updateAboutSection("pillars", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.pillars?.title)}
                        onChange={(e) => updateAboutSection("pillars", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <label className="text-xs text-slate-300">Itens</label>
                      <ButtonSecondaryFix
                        onClick={() =>
                          addAboutListItem("pillars", "items", { icon: "Target", title: "", description: "" })
                        }
                      >
                        Adicionar
                      </ButtonSecondaryFix>
                    </div>
                    <div className="space-y-3">
                      {(draft?.aboutPage?.pillars?.items || []).map((item, idx) => (
                        <div key={`${item?.title || "item"}-${idx}`} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold">#{idx + 1}</div>
                            <Button variant="ghost" onClick={() => removeAboutListItem("pillars", "items", idx)}>
                              Remover
                            </Button>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Ícone</label>
                              <Input
                                value={ensureString(item?.icon)}
                                onChange={(e) => updateAboutListItem("pillars", "items", idx, { icon: e.target.value })}
                                className={inputClass}
                                placeholder="Target, Eye, Heart..."
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Título</label>
                              <Input
                                value={ensureString(item?.title)}
                                onChange={(e) => updateAboutListItem("pillars", "items", idx, { title: e.target.value })}
                                className={inputClass}
                              />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs text-slate-300">Descrição</label>
                              <Textarea
                                value={ensureString(item?.description)}
                                onChange={(e) => updateAboutListItem("pillars", "items", idx, { description: e.target.value })}
                                className={`min-h-20 ${textareaClass}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {(draft?.aboutPage?.pillars?.items || []).length === 0 ? (
                        <div className="text-xs text-slate-400">Sem itens ainda.</div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Linha do tempo</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.timeline?.badge)}
                        onChange={(e) => updateAboutSection("timeline", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.timeline?.title)}
                        onChange={(e) => updateAboutSection("timeline", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <label className="text-xs text-slate-300">Itens</label>
                      <ButtonSecondaryFix
                        onClick={() =>
                          addAboutListItem("timeline", "items", { year: "", title: "", description: "" })
                        }
                      >
                        Adicionar
                      </ButtonSecondaryFix>
                    </div>
                    <div className="space-y-3">
                      {(draft?.aboutPage?.timeline?.items || []).map((item, idx) => (
                        <div key={`${item?.year || "item"}-${idx}`} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold">#{idx + 1}</div>
                            <Button variant="ghost" onClick={() => removeAboutListItem("timeline", "items", idx)}>
                              Remover
                            </Button>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Ano</label>
                              <Input
                                value={ensureString(item?.year)}
                                onChange={(e) => updateAboutListItem("timeline", "items", idx, { year: e.target.value })}
                                className={inputClass}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Título</label>
                              <Input
                                value={ensureString(item?.title)}
                                onChange={(e) => updateAboutListItem("timeline", "items", idx, { title: e.target.value })}
                                className={inputClass}
                              />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs text-slate-300">Descrição</label>
                              <Textarea
                                value={ensureString(item?.description)}
                                onChange={(e) => updateAboutListItem("timeline", "items", idx, { description: e.target.value })}
                                className={`min-h-20 ${textareaClass}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {(draft?.aboutPage?.timeline?.items || []).length === 0 ? (
                        <div className="text-xs text-slate-400">Sem itens ainda.</div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Diferenciais</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.differentialsSection?.badge)}
                        onChange={(e) => updateAboutSection("differentialsSection", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.differentialsSection?.title)}
                        onChange={(e) => updateAboutSection("differentialsSection", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Chamada final</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.cta?.title)}
                        onChange={(e) => updateAboutSection("cta", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.aboutPage?.cta?.description)}
                        onChange={(e) => updateAboutSection("cta", { description: e.target.value })}
                        className={`min-h-20 ${textareaClass}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Botão principal</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.cta?.primaryLabel)}
                        onChange={(e) => updateAboutSection("cta", { primaryLabel: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Botão secundário</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.cta?.secondaryLabel)}
                        onChange={(e) => updateAboutSection("cta", { secondaryLabel: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Link do botão secundário</label>
                      <Input
                        value={ensureString(draft?.aboutPage?.cta?.secondaryHref)}
                        onChange={(e) => updateAboutSection("cta", { secondaryHref: e.target.value })}
                        className={inputClass}
                        placeholder="/contato"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-slate-100">Serviços</h2>
                  <ButtonSecondaryFix onClick={addService}>Adicionar</ButtonSecondaryFix>
                </div>
                <div className="hidden">
                  <div className="lg:col-span-1">
                    <div className="border border-slate-800 rounded-lg bg-slate-950/30 overflow-hidden">
                      <div className="p-3 text-xs text-slate-300 border-b border-slate-800">Artigos</div>
                      <div className="max-h-[70vh] overflow-auto">
                        {(draft?.blogPosts || []).map((p, idx) => {
                          const active = Number(p?.id) === Number(editingPostId);
                          return (
                            <button
                              key={p?.id ?? idx}
                              onClick={() => setEditingPostId(p?.id)}
                              className={`w-full text-left px-3 py-3 border-b border-slate-800 transition-colors ${
                                active ? "bg-orange-500/15" : "hover:bg-white/5"
                              }`}
                            >
                              <div className="text-sm font-semibold text-slate-100 line-clamp-1">
                                {p?.title || `Sem título (#${p?.id ?? idx + 1})`}
                              </div>
                              <div className="text-xs text-slate-400 mt-1 flex items-center justify-between gap-2">
                                <span className="line-clamp-1">{p?.category || "Sem categoria"}</span>
                                <span className="opacity-80">#{p?.id ?? idx + 1}</span>
                              </div>
                            </button>
                          );
                        })}
                        {(draft?.blogPosts || []).length === 0 ? (
                          <div className="p-4 text-sm text-slate-400">Nenhum artigo ainda.</div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    {(() => {
                      const posts = draft?.blogPosts || [];
                      const post = posts.find((p) => Number(p?.id) === Number(editingPostId)) || null;
                      const idx = post ? posts.findIndex((p) => Number(p?.id) === Number(post.id)) : -1;
                      if (!post) {
                        return (
                          <div className="border border-slate-800 rounded-lg p-6 bg-slate-950/30 text-slate-300">
                            Selecione um artigo à esquerda.
                          </div>
                        );
                      }

                      return (
                        <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold text-slate-100">Editando #{post?.id}</div>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                removeListItem("blogPosts", idx);
                                setEditingPostId(null);
                              }}
                            >
                              Remover
                            </Button>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs text-slate-300">Título</label>
                              <Input
                                value={ensureString(post?.title)}
                                onChange={(e) => updateListItemById("blogPosts", post.id, { title: e.target.value })}
                                className={inputClass}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Slug (opcional)</label>
                              <Input
                                value={ensureString(post?.slug)}
                                onChange={(e) => updateListItemById("blogPosts", post.id, { slug: e.target.value })}
                                className={inputClass}
                                placeholder="ex: outsourcing-de-ti"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Categoria</label>
                              <Select
                                value={ensureString(post?.category)}
                                onValueChange={(value) => updateListItemById("blogPosts", post.id, { category: value })}
                              >
                                <SelectTrigger className={inputClass}>
                                  <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {(draft?.blogCategories || []).map((cat) => (
                                    <SelectItem key={cat} value={cat}>
                                      {cat}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs text-slate-300">Data</label>
                              <Input
                                type="date"
                                value={ensureString(post?.date)}
                                onChange={(e) => updateListItemById("blogPosts", post.id, { date: e.target.value })}
                                className={inputClass}
                              />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs text-slate-300">Imagem (URL)</label>
                              <Input
                                value={ensureString(post?.image)}
                                onChange={(e) => updateListItemById("blogPosts", post.id, { image: e.target.value })}
                                className={inputClass}
                              />
                              {post?.image ? (
                                <img
                                  src={post.image}
                                  alt={post.title || "Imagem do post"}
                                  className="w-full max-w-xl h-48 object-cover rounded-md border border-slate-800 mt-2"
                                  loading="lazy"
                                />
                              ) : null}
                              <Input
                                type="file"
                                accept="image/*"
                                className={inputClass}
                                onChange={(e) => uploadBlogImage(post.id, e.target.files?.[0])}
                              />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs text-slate-300">Resumo</label>
                              <Textarea
                                value={ensureString(post?.excerpt)}
                                onChange={(e) => updateListItemById("blogPosts", post.id, { excerpt: e.target.value })}
                                className={`min-h-20 ${textareaClass}`}
                              />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs text-slate-300">Conteúdo (1+ parágrafos)</label>
                              <Textarea
                                value={ensureString(post?.content)}
                                onChange={(e) => updateListItemById("blogPosts", post.id, { content: e.target.value })}
                                className={`min-h-64 ${textareaClass}`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="space-y-4">
                  {(draft?.services || []).map((service, idx) => (
                    <div key={service?.id ?? idx} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-sm font-semibold">#{service?.id ?? idx + 1}</div>
                        <Button variant="ghost" onClick={() => removeListItem("services", idx)}>
                          Remover
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Título</label>
                          <Input
                            value={ensureString(service?.title)}
                            onChange={(e) => updateListItem("services", idx, { title: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Ícone (lucide key)</label>
                          <Input
                            value={ensureString(service?.icon)}
                            onChange={(e) => updateListItem("services", idx, { icon: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Descrição</label>
                          <Textarea
                            value={ensureString(service?.description)}
                            onChange={(e) => updateListItem("services", idx, { description: e.target.value })}
                            className={`min-h-20 ${textareaClass}`}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Imagem (URL)</label>
                          <Input
                            value={ensureString(service?.image)}
                            onChange={(e) => updateListItem("services", idx, { image: e.target.value })}
                            className={inputClass}
                          />
                          {service?.image ? (
                            <img
                              src={service.image}
                              alt={service.title || "Imagem do serviço"}
                              className="w-full max-w-xl h-40 object-cover rounded-md border border-slate-800 mt-2"
                              loading="lazy"
                            />
                          ) : null}
                          <Input
                            type="file"
                            accept="image/*"
                            className={inputClass}
                            onChange={(e) => uploadServiceImage(idx, e.target.files?.[0])}
                          />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Opções (features)</label>
                          <div className="flex items-center gap-2">
                            <Input
                              className={inputClass}
                              placeholder="Adicionar opção e pressionar Enter"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addServiceFeature(idx, e.currentTarget.value);
                                  e.currentTarget.value = "";
                                }
                              }}
                            />
                            <ButtonSecondaryFix
                              onClick={(e) => {
                                const input = e.currentTarget.parentElement?.querySelector("input");
                                addServiceFeature(idx, input?.value);
                                if (input) input.value = "";
                              }}
                            >
                              Adicionar
                            </ButtonSecondaryFix>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {(service?.features || []).map((feature, fIdx) => (
                              <button
                                key={`${service?.id ?? idx}-${fIdx}`}
                                onClick={() => removeServiceFeature(idx, fIdx)}
                                className="px-3 py-1 rounded-full text-xs bg-white/10 hover:bg-white/15 border border-white/10 text-slate-100"
                                title="Remover"
                              >
                                {feature}
                              </button>
                            ))}
                            {(service?.features || []).length === 0 ? (
                              <div className="text-xs text-slate-400">Sem opções ainda.</div>
                            ) : null}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-2">
                            Clique em uma opção para remover.
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-6">
                <h2 className="text-lg font-semibold text-slate-100">Contato</h2>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Hero</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.contactPage?.hero?.badge)}
                        onChange={(e) => updateContactSection("hero", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título (parte 1)</label>
                      <Input
                        value={ensureString(draft?.contactPage?.hero?.title)}
                        onChange={(e) => updateContactSection("hero", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título (destaque)</label>
                      <Input
                        value={ensureString(draft?.contactPage?.hero?.highlight)}
                        onChange={(e) => updateContactSection("hero", { highlight: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.contactPage?.hero?.description)}
                        onChange={(e) => updateContactSection("hero", { description: e.target.value })}
                        className={`min-h-24 ${textareaClass}`}
                      />
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-200">Cards de contato</h3>
                    <ButtonSecondaryFix
                      onClick={() =>
                        addContactListItem("cards", "items", {
                          icon: "Phone",
                          title: "",
                          value: "",
                          link: "",
                          description: ""
                        })
                      }
                    >
                      Adicionar
                    </ButtonSecondaryFix>
                  </div>
                  <div className="space-y-3">
                    {(draft?.contactPage?.cards?.items || []).map((card, idx) => (
                      <div key={`${card?.title || "card"}-${idx}`} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="text-sm font-semibold">#{idx + 1}</div>
                          <Button variant="ghost" onClick={() => removeContactListItem("cards", "items", idx)}>
                            Remover
                          </Button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Ícone</label>
                            <Input
                              value={ensureString(card?.icon)}
                              onChange={(e) =>
                                updateContactListItem("cards", "items", idx, { icon: e.target.value })
                              }
                              className={inputClass}
                              placeholder="Phone, Mail, MessageCircle, MapPin..."
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Título</label>
                            <Input
                              value={ensureString(card?.title)}
                              onChange={(e) =>
                                updateContactListItem("cards", "items", idx, { title: e.target.value })
                              }
                              className={inputClass}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Valor</label>
                            <Input
                              value={ensureString(card?.value)}
                              onChange={(e) =>
                                updateContactListItem("cards", "items", idx, { value: e.target.value })
                              }
                              className={inputClass}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs text-slate-300">Link</label>
                            <Input
                              value={ensureString(card?.link)}
                              onChange={(e) =>
                                updateContactListItem("cards", "items", idx, { link: e.target.value })
                              }
                              className={inputClass}
                              placeholder="https://..."
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <label className="text-xs text-slate-300">Descrição</label>
                            <Input
                              value={ensureString(card?.description)}
                              onChange={(e) =>
                                updateContactListItem("cards", "items", idx, { description: e.target.value })
                              }
                              className={inputClass}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {(draft?.contactPage?.cards?.items || []).length === 0 ? (
                      <div className="text-xs text-slate-400">Sem cards ainda.</div>
                    ) : null}
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Formulário</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Badge</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.badge)}
                        onChange={(e) => updateContactSection("form", { badge: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.title)}
                        onChange={(e) => updateContactSection("form", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.contactPage?.form?.description)}
                        onChange={(e) => updateContactSection("form", { description: e.target.value })}
                        className={`min-h-24 ${textareaClass}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Texto do botão</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.submitLabel)}
                        onChange={(e) => updateContactSection("form", { submitLabel: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Texto enviando</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.sendingLabel)}
                        onChange={(e) => updateContactSection("form", { sendingLabel: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Placeholder Nome</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.fields?.name)}
                        onChange={(e) =>
                          updateContactSection("form", {
                            fields: { ...(draft?.contactPage?.form?.fields || {}), name: e.target.value }
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Placeholder E-mail</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.fields?.email)}
                        onChange={(e) =>
                          updateContactSection("form", {
                            fields: { ...(draft?.contactPage?.form?.fields || {}), email: e.target.value }
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Placeholder Empresa</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.fields?.company)}
                        onChange={(e) =>
                          updateContactSection("form", {
                            fields: { ...(draft?.contactPage?.form?.fields || {}), company: e.target.value }
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Placeholder Telefone</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.fields?.phone)}
                        onChange={(e) =>
                          updateContactSection("form", {
                            fields: { ...(draft?.contactPage?.form?.fields || {}), phone: e.target.value }
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Placeholder Assunto</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.fields?.subject)}
                        onChange={(e) =>
                          updateContactSection("form", {
                            fields: { ...(draft?.contactPage?.form?.fields || {}), subject: e.target.value }
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Placeholder Mensagem</label>
                      <Input
                        value={ensureString(draft?.contactPage?.form?.fields?.message)}
                        onChange={(e) =>
                          updateContactSection("form", {
                            fields: { ...(draft?.contactPage?.form?.fields || {}), message: e.target.value }
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">WhatsApp</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.contactPage?.whatsappCta?.title)}
                        onChange={(e) => updateContactSection("whatsappCta", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.contactPage?.whatsappCta?.description)}
                        onChange={(e) => updateContactSection("whatsappCta", { description: e.target.value })}
                        className={`min-h-20 ${textareaClass}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Texto do botão</label>
                      <Input
                        value={ensureString(draft?.contactPage?.whatsappCta?.buttonLabel)}
                        onChange={(e) => updateContactSection("whatsappCta", { buttonLabel: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Horário de atendimento</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.contactPage?.hours?.title)}
                        onChange={(e) => updateContactSection("hours", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-300">Destaque</label>
                      <Input
                        value={ensureString(draft?.contactPage?.hours?.highlight)}
                        onChange={(e) => updateContactSection("hours", { highlight: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Linhas (1 por linha)</label>
                      <Textarea
                        value={Array.isArray(draft?.contactPage?.hours?.lines)
                          ? draft.contactPage.hours.lines.join("\n")
                          : ""}
                        onChange={(e) =>
                          updateContactSection(
                            "hours",
                            { lines: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) }
                          )
                        }
                        className={`min-h-20 ${textareaClass}`}
                      />
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-200">Área de atuação</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Título</label>
                      <Input
                        value={ensureString(draft?.contactPage?.coverage?.title)}
                        onChange={(e) => updateContactSection("coverage", { title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Descrição</label>
                      <Textarea
                        value={ensureString(draft?.contactPage?.coverage?.description)}
                        onChange={(e) => updateContactSection("coverage", { description: e.target.value })}
                        className={`min-h-20 ${textareaClass}`}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs text-slate-300">Estados (1 por linha)</label>
                      <Textarea
                        value={Array.isArray(draft?.contactPage?.coverage?.states)
                          ? draft.contactPage.coverage.states.join("\n")
                          : ""}
                        onChange={(e) =>
                          updateContactSection(
                            "coverage",
                            { states: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) }
                          )
                        }
                        className={`min-h-20 ${textareaClass}`}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="differentials" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-slate-100">Diferenciais</h2>
                  <ButtonSecondaryFix onClick={addDifferential}>Adicionar</ButtonSecondaryFix>
                </div>
                <div className="space-y-4">
                  {(draft?.differentials || []).map((d, idx) => (
                    <div key={d?.id ?? idx} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-sm font-semibold">#{d?.id ?? idx + 1}</div>
                        <Button variant="ghost" onClick={() => removeListItem("differentials", idx)}>
                          Remover
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Título</label>
                          <Input
                            value={ensureString(d?.title)}
                            onChange={(e) => updateListItem("differentials", idx, { title: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Ícone (lucide key)</label>
                          <Input
                            value={ensureString(d?.icon)}
                            onChange={(e) => updateListItem("differentials", idx, { icon: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Descrição</label>
                          <Textarea
                            value={ensureString(d?.description)}
                            onChange={(e) => updateListItem("differentials", idx, { description: e.target.value })}
                            className={`min-h-20 ${textareaClass}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="testimonials" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-slate-100">Depoimentos</h2>
                  <ButtonSecondaryFix onClick={addTestimonial}>Adicionar</ButtonSecondaryFix>
                </div>
                <div className="space-y-4">
                  {(draft?.testimonials || []).map((t, idx) => (
                    <div key={t?.id ?? idx} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-sm font-semibold">#{t?.id ?? idx + 1}</div>
                        <Button variant="ghost" onClick={() => removeListItem("testimonials", idx)}>
                          Remover
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Foto do cliente</label>
                          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                            <Input
                              value={ensureString(t?.photo)}
                              onChange={(e) => updateListItem("testimonials", idx, { photo: e.target.value })}
                              className={inputClass}
                              placeholder="https://..."
                            />
                            <Input
                              type="file"
                              accept="image/*"
                              className={inputClass}
                              onChange={(e) => uploadTestimonialPhoto(t?.id ?? idx + 1, e.target.files?.[0])}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Nome</label>
                          <Input
                            value={ensureString(t?.name)}
                            onChange={(e) => updateListItem("testimonials", idx, { name: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Empresa</label>
                          <Input
                            value={ensureString(t?.company)}
                            onChange={(e) => updateListItem("testimonials", idx, { company: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Cargo</label>
                          <Input
                            value={ensureString(t?.role)}
                            onChange={(e) => updateListItem("testimonials", idx, { role: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Texto</label>
                          <Textarea
                            value={ensureString(t?.content)}
                            onChange={(e) => updateListItem("testimonials", idx, { content: e.target.value })}
                            className={`min-h-20 ${textareaClass}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blogPosts" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-slate-100">Blog</h2>
                  <ButtonSecondaryFix onClick={addBlogPost}>Adicionar</ButtonSecondaryFix>
                </div>
                <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                  <div className="text-sm font-semibold text-slate-100 mb-3">Categorias</div>
                  <div className="flex items-center gap-2">
                    <Input
                      className={inputClass}
                      placeholder="Nova categoria"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addBlogCategory(e.currentTarget.value);
                          e.currentTarget.value = "";
                        }
                      }}
                    />
                    <ButtonSecondaryFix
                      onClick={(e) => {
                        const input = e.currentTarget.parentElement?.querySelector("input");
                        addBlogCategory(input?.value);
                        if (input) input.value = "";
                      }}
                    >
                      Adicionar
                    </ButtonSecondaryFix>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(draft?.blogCategories || []).map((cat) => (
                      <button
                        key={cat}
                        onClick={() => removeBlogCategory(cat)}
                        className="px-3 py-1 rounded-full text-xs bg-white/10 hover:bg-white/15 border border-white/10 text-slate-100"
                        title="Remover categoria"
                      >
                        {cat}
                      </button>
                    ))}
                    {(draft?.blogCategories || []).length === 0 ? (
                      <div className="text-xs text-slate-400">Sem categorias ainda.</div>
                    ) : null}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-2">Clique em uma categoria para remover.</div>
                </div>
                <div className="space-y-4">
                  {(draft?.blogPosts || []).map((p, idx) => (
                    <div key={p?.id ?? idx} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-sm font-semibold">#{p?.id ?? idx + 1}</div>
                        <Button variant="ghost" onClick={() => removeListItem("blogPosts", idx)}>
                          Remover
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Título</label>
                          <Input
                            value={ensureString(p?.title)}
                            onChange={(e) => updateListItem("blogPosts", idx, { title: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Categoria</label>
                          <Select
                            value={ensureString(p?.category)}
                            onValueChange={(value) => updateListItem("blogPosts", idx, { category: value })}
                          >
                            <SelectTrigger className={inputClass}>
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(draft?.blogCategories || []).map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-slate-300">Data</label>
                          <Input
                            type="date"
                            value={ensureString(p?.date)}
                            onChange={(e) => updateListItem("blogPosts", idx, { date: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Imagem (URL)</label>
                          <Input
                            value={ensureString(p?.image)}
                            onChange={(e) => updateListItem("blogPosts", idx, { image: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Resumo</label>
                          <Textarea
                            value={ensureString(p?.excerpt)}
                            onChange={(e) => updateListItem("blogPosts", idx, { excerpt: e.target.value })}
                            className={`min-h-20 ${textareaClass}`}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs text-slate-300">Conteúdo (1+ parágrafos)</label>
                          <Textarea
                            value={ensureString(p?.content)}
                            onChange={(e) => updateListItem("blogPosts", idx, { content: e.target.value })}
                            className={`min-h-48 ${textareaClass}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="faqs" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-slate-100">FAQ</h2>
                  <ButtonSecondaryFix onClick={addFaq}>Adicionar</ButtonSecondaryFix>
                </div>
                <div className="space-y-4">
                  {(draft?.faqs || []).map((f, idx) => (
                    <div key={idx} className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-sm font-semibold">Pergunta {idx + 1}</div>
                        <Button variant="ghost" onClick={() => removeListItem("faqs", idx)}>
                          Remover
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-slate-300">Pergunta</label>
                        <Input
                          value={ensureString(f?.question)}
                          onChange={(e) => updateListItem("faqs", idx, { question: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div className="space-y-2 mt-3">
                        <label className="text-xs text-slate-300">Resposta</label>
                        <Textarea
                          value={ensureString(f?.answer)}
                          onChange={(e) => updateListItem("faqs", idx, { answer: e.target.value })}
                          className={`min-h-20 ${textareaClass}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="video" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <h2 className="text-lg font-semibold text-slate-100">Vídeo institucional</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs text-slate-300">Capa (URL)</label>
                    <Input
                      value={ensureString(draft?.institutionalVideo?.coverUrl)}
                      onChange={(e) => updateInstitutionalVideo("coverUrl", e.target.value)}
                      className={inputClass}
                    />
                    {draft?.institutionalVideo?.coverUrl ? (
                      <img
                        src={draft.institutionalVideo.coverUrl}
                        alt="Capa do vídeo"
                        className="w-full max-w-xl h-48 object-cover rounded-md border border-slate-800 mt-2"
                        loading="lazy"
                      />
                    ) : null}
                    <Input
                      type="file"
                      accept="image/*"
                      className={inputClass}
                      onChange={(e) => uploadInstitutionalCover(e.target.files?.[0])}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs text-slate-300">Link do vídeo (mp4/webm/mov, YouTube ou Vimeo)</label>
                    <Input
                      value={ensureString(draft?.institutionalVideo?.videoUrl)}
                      onChange={(e) => updateInstitutionalVideo("videoUrl", e.target.value)}
                      className={inputClass}
                      placeholder="https://..."
                    />
                    <Input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      className={inputClass}
                      onChange={(e) => uploadInstitutionalVideoFile(e.target.files?.[0])}
                    />
                    <div className="text-[11px] text-slate-400">
                      Upload salva em `/uploads` no backend; link externo também funciona.
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs text-slate-300">Duração (texto)</label>
                    <Input
                      value={ensureString(draft?.institutionalVideo?.durationLabel)}
                      onChange={(e) => updateInstitutionalVideo("durationLabel", e.target.value)}
                      className={inputClass}
                      placeholder="1:30"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="images" className="space-y-6">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-5 space-y-4">
                <h2 className="text-lg font-semibold text-slate-100">Imagens (URLs)</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(draft?.images || {}).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <label className="text-xs text-slate-300">{key}</label>
                      <Input
                        value={ensureString(value)}
                        onChange={(e) => updateImages(key, e.target.value)}
                        className={inputClass}
                      />
                      {value ? (
                        <img
                          src={value}
                          alt={key}
                          className="w-full max-w-sm h-32 object-cover rounded-md border border-slate-800"
                          loading="lazy"
                        />
                      ) : null}
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          className={inputClass}
                          onChange={(e) => uploadImage(key, e.target.files?.[0])}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="json" className="space-y-4">
            <Card className="border-slate-800 bg-slate-900">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between mb-3">
                  <div className="text-xs text-slate-400">Status: {parsed ? "JSON ok" : "JSON inválido"}</div>
                  <div className="flex items-center gap-2">
                    <ButtonSecondaryFix onClick={syncJsonFromDraft}>
                      Puxar da dashboard
                    </ButtonSecondaryFix>
                    <ButtonSecondaryFix onClick={applyJsonToDraft}>
                      Aplicar na dashboard
                    </ButtonSecondaryFix>
                  </div>
                </div>
                <Separator className="bg-slate-800 mb-3" />
                <Textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  className="min-h-[70vh] font-mono text-xs bg-slate-950 border-slate-800 text-slate-100"
                  spellCheck={false}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
